// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@rpsc/MobiusOverrides/Button/Button";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import React, { FC } from "react";
import { connect } from "react-redux";

interface WelcomeAccountStyles {
    wrapper?: InjectableCss;
    customerWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    customerName?: TypographyProps;
    customerNumber?: TypographyProps;
    link?: LinkPresentationProps;
    ctaButton?: ButtonPresentationProps;
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        currentBillTo: getBillToState(state, session.billToId).value,
        firstName: session.firstName || session.userLabel || "",
        headerHeight: (state as CustomAppState).xHeader.headerHeight,
    };
};

export const welcomeAccountStyles: WelcomeAccountStyles = {};

const styles = welcomeAccountStyles;

const enum fields {
    showActions = "showActions",
    link = "link",
    linkLabel = "linkLabel",
    ctaRowId = "ctaRowId",
    ctaLabel = "ctaLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showActions]: boolean;
        [fields.link]: LinkFieldValue;
        [fields.linkLabel]: string;
        [fields.ctaRowId]: string;
        [fields.ctaLabel]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const WelcomeAccount: FC<Props> = ({ headerHeight, currentBillTo, firstName, fields }) => {
    const { showActions, link, linkLabel, ctaRowId, ctaLabel } = fields;
    const { url, title } = useGetLink(link);

    const handleScrollTo = () => {
        const el = document.getElementById(ctaRowId);

        if (el) {
            const headerHeightVal = parseInt(headerHeight || "0", 10);
            const scrollTop = el.getBoundingClientRect().top + window.scrollY - headerHeightVal;
            window.scrollTo({ behavior: "smooth", top: scrollTop });
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.customerWrapper} className="customer-wrapper">
                <Typography {...styles.customerName}>{translate("Hello, {0}", firstName)}</Typography>
                <Typography {...styles.customerNumber} className="customer-number">
                    {translate("Customer: #{0}", currentBillTo?.customerNumber || "")}
                </Typography>
            </StyledWrapper>
            {showActions && (
                <StyledWrapper {...styles.actionsWrapper}>
                    <Link href={url} aria-label={linkLabel || title} {...styles.link}>
                        {linkLabel || title}
                    </Link>
                    {ctaRowId && (
                        <Button {...styles.ctaButton} onClick={handleScrollTo} aria-label={ctaLabel}>
                            {ctaLabel}
                        </Button>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
const WelcomeAccountWidget: WidgetModule = {
    component: connect(mapStateToProps)(WelcomeAccount),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.showActions,
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                fieldType: "Contextual",
                name: fields.link,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                isVisible: ({ fields }) => fields.showActions,
            },
            {
                fieldType: "Contextual",
                name: fields.linkLabel,
                editorTemplate: "TextField",
                displayName: "Link Label",
                defaultValue: "Visit Dashboard",
                isVisible: ({ fields }) => fields.showActions,
            },
            {
                fieldType: "Contextual",
                name: fields.ctaRowId,
                editorTemplate: "TextField",
                displayName: "CTA Connected Row Id",
                defaultValue: "",
                isVisible: ({ fields }) => fields.showActions,
            },
            {
                fieldType: "Contextual",
                name: fields.ctaLabel,
                editorTemplate: "TextField",
                displayName: "CTA Label",
                defaultValue: "Account Quick View",
                isVisible: ({ fields }) => fields.showActions,
            },
        ],
    },
};
export default WelcomeAccountWidget;
